/**
 * useAnalyticsData Hook
 * Fetches Google Analytics data from the backend API
 */

import { useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

const useAnalyticsData = () => {
  const [pageViews, setPageViews] = useState(null);
  const [topPages, setTopPages] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [realTime, setRealTime] = useState(null);
  const [loading, setLoading] = useState({
    pageViews: false,
    topPages: false,
    demographics: false,
    realTime: false
  });
  const [error, setError] = useState(null);

  /**
   * Fetch page views report
   */
  const fetchPageViews = async (startDate, endDate) => {
    setLoading(prev => ({ ...prev, pageViews: true }));
    setError(null);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const response = await api.get('/analytics/page-views', { params });
      setPageViews(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to fetch page views';
      setError(errorMessage);
      // Only show toast for non-configuration errors
      if (!errorMessage.includes('not configured') && !errorMessage.includes('unavailable')) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(prev => ({ ...prev, pageViews: false }));
    }
  };

  /**
   * Fetch top pages report
   */
  const fetchTopPages = async (limit = 10) => {
    setLoading(prev => ({ ...prev, topPages: true }));
    setError(null);
    try {
      const response = await api.get('/analytics/top-pages', {
        params: { limit }
      });
      setTopPages(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to fetch top pages';
      setError(errorMessage);
      if (!errorMessage.includes('not configured') && !errorMessage.includes('unavailable')) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(prev => ({ ...prev, topPages: false }));
    }
  };

  /**
   * Fetch user demographics report
   */
  const fetchDemographics = async () => {
    setLoading(prev => ({ ...prev, demographics: true }));
    setError(null);
    try {
      const response = await api.get('/analytics/demographics');
      setDemographics(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to fetch demographics';
      setError(errorMessage);
      if (!errorMessage.includes('not configured') && !errorMessage.includes('unavailable')) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(prev => ({ ...prev, demographics: false }));
    }
  };

  /**
   * Fetch real-time report
   */
  const fetchRealTime = async () => {
    setLoading(prev => ({ ...prev, realTime: true }));
    setError(null);
    try {
      const response = await api.get('/analytics/realtime');
      setRealTime(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to fetch real-time data';
      setError(errorMessage);
      if (!errorMessage.includes('not configured') && !errorMessage.includes('unavailable')) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(prev => ({ ...prev, realTime: false }));
    }
  };

  /**
   * Refresh all data
   */
  const refreshAll = async (startDate, endDate) => {
    await Promise.all([
      fetchPageViews(startDate, endDate),
      fetchTopPages(10),
      fetchDemographics(),
      fetchRealTime()
    ]);
  };

  return {
    pageViews,
    topPages,
    demographics,
    realTime,
    loading,
    error,
    fetchPageViews,
    fetchTopPages,
    fetchDemographics,
    fetchRealTime,
    refreshAll
  };
};

export default useAnalyticsData;

